import { CheckStatus, Parse } from '../utils/ajax';
import Model from '../utils/model';

export const getService = ({ token, tenantId, serviceId }) => {
    
    const url = `/api/draw/1/element/service/${serviceId}`;

    const request = {
        headers: {
            Authorization: token,
            ManyWhoTenant: tenantId,
        },
    };

    return fetch(url, request)
        .then(CheckStatus)
        .then(Parse);
};

export const saveService = ({ token, serviceData }) => {
    
    const url = `${manywho.settings.global('platform.uri')}/api/draw/1/element/service`;

    const request = {
        headers: {
            Authorization: token,
            'Content-Type': 'application/json; charset=utf-8',
        },
        method: 'POST',
        body: JSON.stringify(serviceData),
    };

    return fetch(url, request)
        .then(CheckStatus)
        .then(Parse);
};

export const deleteService = ({ token, serviceId }) => {
    
    const url = `/api/draw/1/element/service/${serviceId}`;

    const request = {
        headers: {
            Authorization: token,
            'Content-Type': 'application/json; charset=utf-8',
        },
        method: 'DELETE',
    };

    return fetch(url, request)
        .then(CheckStatus)
        .then(Parse);
};

export const getAllServices = ({ token, tenantId }) => {
    
    const url = '/api/draw/1/element/service';

    const request = {
        headers: {
            Authorization: token,
            ManyWhoTenant: tenantId,
        },
    };

    return fetch(url, request)
        .then(CheckStatus)
        .then(Parse);
};

/**
 * 
 * If a search term is provided, then the servce wil attampt to return results based on that.
 * If objectData is provided then the service will attempt to filter results based on that.
 * So for example, to return specific users the objectData might look like:
    
    [
        {
            developerName: "GroupAuthorizationUser",
            properties: [
                {
                    developerName: "AuthenticationId",
                    contentType: "ContentString",
                    contentValue: "1"
                }
            ]
        },
        {
            developerName: "GroupAuthorizationUser",
            properties: [
                {
                    developerName: "AuthenticationId",
                    contentType: "ContentString",
                    contentValue: "2"
                }
            ]
        }
    ]
 */
export const getServiceGroupsOrUsers = ({ token, tenantId, serviceId, type, search = null, objectData = null }) => {
    
    const url = '/plugins/manywho/api/draw/2/data';

    const body = {
        configurationValues: [
            {
                developerName: 'AuthenticationToken',
                contentValue: token,
            },
            {
                developerName: 'FlowTenantId',
                contentValue: tenantId,
            },
        ],
        objectDataType: {
            developerName: type === 'group' ? 'GroupAuthorizationGroup' : 'GroupAuthorizationUser',
        },
        objectData,
        listFilter: {
            comparisonType: 'VALUE',
            limit: 1000,
            filterByProvidedObjects: objectData !== null, // Used by some services to tell it to filter by objectdata
            search,
            where: [
                {
                    columnName: 'ServiceElementId',
                    criteriaType: 'EQUAL',
                    value: serviceId,
                },
            ],
        },
        stateId: Model.guid(),
    };

    const request = {
        headers: {
            Authorization: token,
            'Content-Type': 'application/json; charset=utf-8',
        },
        method: 'POST',
        body: JSON.stringify(body),
    };

    return fetch(url, request)
        .then(CheckStatus)
        .then(Parse);
};

export const getServiceAuthenticationAttributes = ({ token, tenantId, serviceId, type }) => {
    
    const url = '/plugins/manywho/api/draw/2/getauthenticationattributes';

    const body = {
        configurationValues: [
            {
                developerName: 'EditingToken',
                contentValue: 'blank',
            },
            {
                developerName: 'AuthenticationToken',
                contentValue: token,
            },
            {
                developerName: 'FlowTenantId',
                contentValue: tenantId,
            },
        ],
        inputs: [
            {
                developerName: 'Kind',
                contentValue: type, // group or user
            },
            {
                developerName: 'ServiceElementId',
                contentValue: serviceId,
            },
        ],
        stateId: Model.guid(),
    };

    const request = {
        headers: {
            Authorization: token,
            'Content-Type': 'application/json; charset=utf-8',
        },
        method: 'POST',
        body: JSON.stringify(body),
    };

    return fetch(url, request)
        .then(CheckStatus)
        .then(Parse);
};

export const getServiceConfigurationValues = ({ token, tenantId, uri, basicUsername = null, basicPassword = null }) => {
    
    const url = `${manywho.settings.global('platform.uri')}/plugins/manywho/api/draw/2/refreshservice`;

    const body = {
        configurationValues: [
            {
                contentValue: 'blank',
                developerName: 'EditingToken',
            },
            {
                contentValue: tenantId,
                developerName: 'FlowTenantId',
            },
            {
                contentValue: token,
                developerName: 'AuthenticationToken',
            },
        ],
        inputs: [
            {
                contentValue: basicUsername,
                developerName: 'HTTP Basic Authentication Username',
            },
            {
                contentValue: basicPassword,
                developerName: 'HTTP Basic Authentication Password',
            },
            {
                contentValue: uri,
                developerName: 'Service Uri',
            },
        ],
        stateId: Model.guid(),
    };

    const request = {
        headers: {
            Authorization: token,
            'Content-Type': 'application/json; charset=utf-8',
        },
        method: 'POST',
        body: JSON.stringify(body),
    };

    return fetch(url, request)
        .then(CheckStatus)
        .then(Parse);
};

export const getServiceTypesAndActions = ({ token, tenantId, uri, populatedServiceValueRequests, basicUsername = null, basicPassword = null }) => {
    
    const url = `${manywho.settings.global('platform.uri')}/plugins/manywho/api/draw/2/gettypesandactions`;

    const body = {
        configurationValues: [
            {
                contentValue: 'blank',
                developerName: 'EditingToken',
            },
            {
                contentValue: tenantId,
                developerName: 'FlowTenantId',
            },
            {
                contentValue: token,
                developerName: 'AuthenticationToken',
            },
        ],
        inputs: [
            {
                developerName: 'Service Element',
                objectData: [
                    {
                        developerName: 'ServiceElementRequest',
                        properties: [
                            {
                                contentValue: uri,
                                developerName: 'Uri',
                            },
                            {
                                contentValue: 'json',
                                developerName: 'Format',
                            },
                            {
                                developerName: 'ConfigurationValues',
                                objectData: populatedServiceValueRequests,
                            },
                            {
                                contentValue: basicUsername,
                                developerName: 'HttpAuthenticationUsername',
                            },
                            {
                                contentValue: basicPassword,
                                developerName: 'HttpAuthenticationPassword',
                            },
                            {
                                contentValue: 'SERVICE',
                                developerName: 'ElementType',
                            },
                        ],
                    },
                ],
            },
        ],
        stateId: Model.guid(),
    };

    const request = {
        headers: {
            Authorization: token,
            'Content-Type': 'application/json; charset=utf-8',
        },
        method: 'POST',
        body: JSON.stringify(body),
    };

    return fetch(url, request)
        .then(CheckStatus)
        .then(Parse);
};

export const installService = ({
    token,
    tenantId,
    uri,
    id = Model.guid(),
    developerName,
    developerSummary,
    populatedServiceValueRequests,
    basicUsername = null,
    basicPassword = null,
}) => {
    
    const url = `${manywho.settings.global('platform.uri')}/plugins/manywho/api/draw/2/installservice`;

    const body = {
        configurationValues: [
            {
                contentValue: 'blank',
                developerName: 'EditingToken',
            },
            {
                contentValue: tenantId,
                developerName: 'FlowTenantId',
            },
            {
                contentValue: token,
                developerName: 'AuthenticationToken',
            },
        ],
        inputs: [
            {
                developerName: 'Service Element',
                objectData: [
                    {
                        developerName: 'ServiceElementRequest',
                        properties: [
                            {
                                contentValue: uri,
                                developerName: 'Uri',
                            },
                            {
                                contentValue: 'json',
                                developerName: 'Format',
                            },
                            {
                                developerName: 'ConfigurationValues',
                                objectData: populatedServiceValueRequests,
                            },
                            {
                                contentValue: basicUsername,
                                developerName: 'HttpAuthenticationUsername',
                            },
                            {
                                contentValue: basicPassword,
                                developerName: 'HttpAuthenticationPassword',
                            },
                            {
                                contentValue: id,
                                developerName: 'Id',
                            },
                            {
                                contentValue: 'SERVICE',
                                developerName: 'ElementType',
                            },
                            {
                                contentValue: developerName,
                                developerName: 'DeveloperName',
                            },
                            {
                                contentValue: developerSummary,
                                developerName: 'DeveloperSummary',
                            },
                        ],
                    },
                ],
            },
        ],
        stateId: Model.guid(),
    };

    const request = {
        headers: {
            Authorization: token,
            'Content-Type': 'application/json; charset=utf-8',
        },
        method: 'POST',
        body: JSON.stringify(body),
    };

    return fetch(url, request)
        .then(CheckStatus)
        .then(Parse);
};
